import { FormControlLabel, Switch, Typography } from "@mui/material";
import { Box, Grid, Stack } from "@mui/system";
import { Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BudgetControlChart from "./BudgetControlChart";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import { setIsBudgetOverused } from "../../../../../features/bizDevelopmentDashboardSlice";

const BudgetControlGraphView = () => {
  const dispatch = useDispatch();
  //is enabled budget overuse toggle
  const budgetOveruse = useSelector(
    (state: any) => state.bizDevelopmentDashboard.isBudgetOverused
  );
  const [selectedBar, setSelectedBar] = useState<any | null>(null);

  const budgetDataOfBizDevelopment = useSelector(
    (state: any) =>
      state.bizDevelopmentDashboard.budgetDataOfBizDevelopment ?? []
  );

  //fetch brand name for budget from redux
  const brandNameForBudget = useSelector(
    (state: any) => state.bizDevelopmentDashboard.brandNameForBudget
  );

  const filteredData = useMemo(() => {
    const search = (brandNameForBudget ?? "").trim().toLowerCase();
    if (!search) return budgetDataOfBizDevelopment;
    return budgetDataOfBizDevelopment.filter((item: any) =>
      String(item["brand_display_name"] ?? "")
        .toLowerCase()
        .includes(search)
    );
  }, [budgetDataOfBizDevelopment, brandNameForBudget]);

  const chartData = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [];
    return filteredData
      .map((item: any) => ({
        name: item["brand_display_name"],
        value: item["perc_of_budget_used"]
          ? item["perc_of_budget_used"] * 100
          : 0,
      }))
      .sort((a: any, b: any) => a.name.localeCompare(b.name));
  }, [filteredData]);

  const handleBarClick = (barData: any) => {
    const matched = budgetDataOfBizDevelopment?.find(
      (item: any) => item["brand_display_name"] === barData.name
    );
    // toggle off if same bar clicked again
    setSelectedBar((prev: any) =>
      prev?.["brand_display_name"] === barData.name ? null : matched ?? null
    );
  };

  //Current requirements(https://optival.atlassian.net/browse/GR-21074)
  //negative value should be displayed as -$100
  //positive value should be displayed as $100
  // used 1000 seperator and displayed as $1,000
  // numver should be displayed as whole number(rounded number)
  const formatMoney = (val: any) => {
    if (val === undefined || val === null) return "";
    const num = Number(val);
    const formatted = Math.abs(num).toLocaleString(undefined, {
      maximumFractionDigits: 0,
    });

    //return num < 0 ? `-$${formatted}` : `$${formatted}`;
    return num < 0 ? `-${formatted}` : formatted; // according to figma design updated
  };

  //rounded down to the nearest integer(https://optival.atlassian.net/browse/GR-21152)
  // QFTD budget: rounded down to nearest integer; empty when missing
  const roundDownBudget = (value: unknown): string => {
    if (value === null || value === undefined || value === "") return "";
    const num = Number(value);
    // return Number.isFinite(num)
    //   ? `$${Math.floor(num).toLocaleString("en-US")}`
    //   : "";

    return Number.isFinite(num)
      ? `${Math.floor(num).toLocaleString("en-US")}`
      : ""; // according to figma design updated
  };

  const formatPerc = (val: any) =>
    val !== undefined && val !== null
      ? `${parseFloat(Number(val).toFixed(2))}%`
      : "";

  const formatPercRounded = (val: any) =>
    val !== undefined && val !== null ? `${Math.round(Number(val))}%` : "";

  const percBadge = (val: any) => {
    const num = parseFloat(val);
    const isOver = num >= 100;
    return (
      <Box
        sx={{
          backgroundColor: isOver ? "#FDEDED" : "#EDF7ED",
          color: isOver ? "#C62828" : "#2E7D32",
          border: `1px solid ${
            isOver ? "rgba(198,40,40,0.5)" : "rgba(46,125,50,0.5)"
          }`,
          borderRadius: "4px",
          px: "8px",
          py: "1px",
          fontSize: "13px",
          fontWeight: 400,
          whiteSpace: "nowrap",
        }}
      >
        {formatPerc(val)}
      </Box>
    );
  };

  // Graph details view
  const detailRows = selectedBar
    ? [
        // Row 1
        [
          {
            label: "Budget Used",
            value: formatPercRounded(selectedBar["perc_of_budget_used"] * 100),
          },
          {
            label: "% QFTD Used",
            value: selectedBar["perc_of_qftd_budget_used"]
              ? percBadge(selectedBar["perc_of_qftd_budget_used"] * 100)
              : "",
            isBadge: true,
          },
          {
            label: "Total Income",
            value: selectedBar["total_income"]
              ? formatMoney(selectedBar["total_income"])
              : "",
          },
        ],
        // Row 2
        [
          {
            label: "CPA Income",
            value: selectedBar["cpa_income"]
              ? formatMoney(selectedBar["cpa_income"])
              : "",
          },
          {
            label: "QFTD Budget",
            tooltip: "Based on the CPA budget divided by the current deal",
            value:
              selectedBar["qftd_budget"] != null &&
              selectedBar["qftd_budget"] !== ""
                ? roundDownBudget(selectedBar["qftd_budget"])
                : "",
          },
          { label: "FTD", value: selectedBar["ftd"] ?? "" },
        ],
        // Row 3
        [
          {
            label: "CPA Budget",
            value: selectedBar["cpa_constraint"]
              ? formatMoney(selectedBar["cpa_constraint"])
              : "",
          },
          { label: "QFTD", value: selectedBar["qftd"] ?? "" },
          { label: "", value: "" },
        ],
        // Row 4
        [
          {
            label: "RR Budget Used",
            value: selectedBar["perc_of_qftd_RR_budget_use"]
              ? percBadge(selectedBar["perc_of_qftd_RR_budget_use"] * 100)
              : "",
            isBadge: true,
          },
          {
            label: "Remaining QFTD",
            value:
              selectedBar["remaining_qftd"] != null
                ? Math.trunc(Number(selectedBar["remaining_qftd"]))
                : "",
          },
          { label: "", value: "" },
        ],
      ]
    : [];

  useEffect(() => {
    if (
      selectedBar &&
      !filteredData.some(
        (item: any) =>
          item["brand_display_name"] === selectedBar["brand_display_name"]
      )
    ) {
      setSelectedBar(null);
    }
  }, [filteredData, selectedBar]);

  if (!filteredData || filteredData.length === 0) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height={380}
      >
        <Typography fontSize="14px" color="text.secondary">
          No data available.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Header row */}
      <Box
        px={2}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        pb={2}
      >
        <Typography variant="body2" fontSize="12px" color="text.secondary">
          Click on a Bar to see the full Details, Or switch to Table View
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center">
          <Typography fontSize="14px">Budget Overuse</Typography>
          <FormControlLabel
            control={
              <Switch
                data-tracker="821"
                color="secondary"
                size="medium"
                checked={budgetOveruse}
                onChange={(e) => {
                  dispatch(setIsBudgetOverused(e.target.checked));
                  setSelectedBar(null);
                }}
              />
            }
            label=""
            sx={{ mr: 0 }}
          />
        </Stack>
      </Box>

      {/* Chart */}
      <BudgetControlChart
        data={chartData}
        showOveruseOnly={budgetOveruse}
        onBarClick={handleBarClick}
        activeBar={selectedBar ? selectedBar["brand_display_name"] : null}
      />

      {/* Detail panel — shown on specific chart bar click */}
      <Box sx={{ px: 2, pt: 2, minHeight: 200 }}>
        {selectedBar && (
          <>
            {/* <Box sx={{ px: 2, pt: 2 }}></Box> */}
            <Typography fontSize="18px" fontWeight={500} mb={2}>
              {selectedBar["brand_display_name"]}
            </Typography>

            {detailRows.map((row, rowIdx) => (
              <Grid
                key={rowIdx}
                container
                sx={{
                  borderBottom:
                    rowIdx < detailRows.length - 1
                      ? "1px solid #F0F0F0"
                      : "none",
                  py: 1,
                }}
              >
                {row.map((cell, colIdx) => (
                  <Grid key={colIdx} size={4}>
                    {cell.label ? (
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        pr={4}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                        >
                          <Typography
                            fontSize="13px"
                            fontWeight={500}
                            color="text.primary"
                          >
                            {cell.label}
                          </Typography>
                          {(cell as any).tooltip && (
                            <Tooltip
                              title={(cell as any).tooltip}
                              arrow
                              placement="top"
                              slotProps={{
                                tooltip: {
                                  sx: {
                                    maxWidth: 160,
                                    whiteSpace: "normal",
                                    textAlign: "center",
                                  },
                                },
                              }}
                            >
                              <InfoOutlinedIcon
                                sx={{
                                  fontSize: 16,
                                  color: "gray",
                                  cursor: "pointer",
                                }}
                              />
                            </Tooltip>
                          )}
                        </Stack>
                        {(cell as any).isBadge ? (
                          cell.value
                        ) : (
                          <Typography fontSize="13px" color="text.secondary">
                            {cell.value}
                          </Typography>
                        )}
                      </Stack>
                    ) : null}
                  </Grid>
                ))}
              </Grid>
            ))}
          </>
        )}
      </Box>
    </>
  );
};
export default BudgetControlGraphView;
